import { Fragment } from 'react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { menu } from '~/lib/menu';
import type { Crumb } from '~/lib/store/breadcrumbs';
import { useBreadcrumbStore } from '~/lib/store/breadcrumbs';

/**
 * Walk the menu tree for the longest path matching the current location.
 *
 * Reads the **unfiltered** `menu` on purpose — not `visibleMenu`. This resolves the heading for the route the
 * administrator is already on, so filtering it would blank the crumb on `/returns` at exactly the moment the
 * "module disabled" page needs a title. Navigation decides what you can *go* to; a breadcrumb only names
 * where you *are*.
 */
function deriveCrumbs(pathname: string): Crumb[] {
	let best: { crumbs: Crumb[]; matchLen: number; matchedPath: string } | null = null;

	for (const section of menu) {
		for (const item of section.items) {
			const candidates: { path: string; trail: Crumb[] }[] = [];
			if (item.path) {
				candidates.push({ path: item.path, trail: [{ labelKey: section.labelKey }, { labelKey: item.labelKey, path: item.path }] });
			}
			for (const child of item.children ?? []) {
				candidates.push({
					path: child.path,
					trail: [{ labelKey: section.labelKey }, { labelKey: item.labelKey }, { labelKey: child.labelKey, path: child.path }]
				});
			}
			for (const c of candidates) {
				const isMatch = pathname === c.path || pathname.startsWith(c.path + '/');
				if (isMatch && c.path.length > (best?.matchLen ?? -1)) {
					best = { crumbs: c.trail, matchLen: c.path.length, matchedPath: c.path };
				}
			}
		}
	}

	if (!best) return [];
	const crumbs = [...best.crumbs];
	// Detail segment beyond the matched menu path (e.g. /promotions/123 → "#123").
	if (pathname.length > best.matchedPath.length) {
		const rest = pathname.slice(best.matchedPath.length + 1).split('/')[0];
		if (rest) crumbs.push({ label: `#${rest}` });
	}
	return crumbs;
}

/** Topbar breadcrumb trail. Uses a page override when set, else derives from the route. */
export function Breadcrumbs() {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const override = useBreadcrumbStore(s => s.override);
	const crumbs = override ?? deriveCrumbs(pathname);

	if (crumbs.length === 0) return null;

	return (
		<nav aria-label="breadcrumb" className="flex min-w-0 items-center gap-1 text-xs">
			{crumbs.map((crumb, i) => {
				const isLast = i === crumbs.length - 1;
				const text = crumb.label ?? (crumb.labelKey ? t(crumb.labelKey) : '');
				return (
					<Fragment key={`${crumb.labelKey ?? crumb.label}-${i}`}>
						{i > 0 && <ChevronRight size={12} className="shrink-0 text-foreground-subtle" />}
						{crumb.path && !isLast ? (
							<Link to={crumb.path} className="truncate text-foreground-muted hover:text-foreground">
								{text}
							</Link>
						) : (
							<span className={isLast ? 'truncate font-medium text-foreground' : 'truncate text-foreground-muted'}>{text}</span>
						)}
					</Fragment>
				);
			})}
		</nav>
	);
}
